<script setup lang="ts">
// ヒストグラム + 情報 (カーソル位置の色、選択範囲、文書サイズ)。
// 合成結果は変更後に遅延して縮小読み出しする (最大 256px)。
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useUiStore } from '@/stores/uiStore'
import { useI18n } from '@/i18n'
import { readCompositeCached, sampleComposite } from '@/app/engine'
import { resampleRgba } from '@/io/imageEncode'
import { computeHistogram, type Histogram } from '@/color/histogram'
import { rgbToHex, rgbToHsv } from '@/color/convert'

const docStore = useDocumentStore()
const ui = useUiStore()
const { t } = useI18n()
const canvas = ref<HTMLCanvasElement | null>(null)
const channel = ref<'lum' | 'r' | 'g' | 'b' | 'rgb'>('lum')
const hist = ref<Histogram | null>(null)
let timer: number | null = null
let stop: (() => void) | null = null

function recompute() {
  if (!docStore.doc) {
    hist.value = null
    draw()
    return
  }
  try {
    let px = readCompositeCached()
    if (!px) {
      hist.value = null
      draw()
      return
    }
    const scale = Math.min(1, 256 / Math.max(px.width, px.height))
    if (scale < 1) px = resampleRgba(px.width, px.height, px.rgba, scale)
    hist.value = computeHistogram(px.rgba)
  } catch {
    hist.value = null
  }
  draw()
}
function schedule() {
  if (timer !== null) clearTimeout(timer)
  timer = window.setTimeout(() => {
    timer = null
    recompute()
  }, 400)
}
function draw() {
  const c = canvas.value
  if (!c) return
  const dpr = window.devicePixelRatio || 1
  const W = c.clientWidth || 260, H = 80
  c.width = W * dpr
  c.height = H * dpr
  const ctx = c.getContext('2d')!
  ctx.scale(dpr, dpr)
  ctx.clearRect(0, 0, W, H)
  const h = hist.value
  if (!h) return
  const series: { data: Uint32Array; max: number; color: string }[] =
    channel.value === 'rgb'
      ? [{ data: h.r, max: h.max.r, color: 'rgba(255,80,80,0.6)' }, { data: h.g, max: h.max.g, color: 'rgba(80,220,80,0.6)' }, { data: h.b, max: h.max.b, color: 'rgba(90,140,255,0.6)' }]
      : [{ data: h[channel.value], max: h.max[channel.value], color: getComputedStyle(document.documentElement).getPropertyValue('--fg-1').trim() || '#ccc' }]
  for (const s of series) {
    const m = Math.max(1, s.max)
    ctx.fillStyle = s.color
    for (let i = 0; i < 256; i++) {
      const v = (s.data[i]! / m) * (H - 2)
      const x = (i / 256) * W
      ctx.fillRect(x, H - v, Math.max(1, W / 256), v)
    }
  }
}
watch(channel, draw)
onMounted(() => {
  stop = docStore.onChange(() => schedule())
  schedule()
  window.addEventListener('resize', draw)
})
onBeforeUnmount(() => {
  stop?.()
  if (timer !== null) clearTimeout(timer)
  window.removeEventListener('resize', draw)
})
watch(() => docStore.sessionVersion, schedule)

const cursorInfo = computed(() => {
  const p = ui.cursorDocPos
  if (!p || !docStore.doc) return null
  const px = sampleComposite(p.x, p.y)
  if (!px) return null
  const rgb = { r: px[0], g: px[1], b: px[2] }
  const hsv = rgbToHsv(rgb)
  return { x: Math.floor(p.x), y: Math.floor(p.y), hex: rgbToHex(rgb), rgb, a: px[3], hsv }
})
const selInfo = computed(() => {
  void docStore.historyVersion
  const m = docStore.hasSelection ? docStore.selectionMask() : null
  return m ? m.rect : null
})
</script>

<template>
  <div class="panel">
    <div class="panel-title row">
      <span>{{ t('panel.histogram') }}</span>
      <span class="spacer" />
      <select v-model="channel" class="ch">
        <option value="lum">{{ t('histogram.lum') }}</option>
        <option value="rgb">RGB</option>
        <option value="r">R</option>
        <option value="g">G</option>
        <option value="b">B</option>
      </select>
    </div>
    <canvas ref="canvas" class="hist" />
    <div class="info mono">
      <span v-if="hist">{{ t('histogram.mean') }} {{ hist.mean.toFixed(1) }} · {{ t('histogram.median') }} {{ hist.median }}</span>
      <span v-if="cursorInfo">
        <span class="sw" :style="{ background: cursorInfo.hex }" /> {{ cursorInfo.hex }} R{{ cursorInfo.rgb.r }} G{{ cursorInfo.rgb.g }} B{{ cursorInfo.rgb.b }} A{{ cursorInfo.a }} · H{{ Math.round(cursorInfo.hsv.h) }} S{{ Math.round(cursorInfo.hsv.s) }} B{{ Math.round(cursorInfo.hsv.v) }}
      </span>
      <span v-if="selInfo">{{ t('histogram.selection') }} {{ selInfo.x }},{{ selInfo.y }} {{ selInfo.w }}×{{ selInfo.h }}</span>
    </div>
  </div>
</template>

<style scoped>
.panel {
  display: flex;
  flex-direction: column;
}
.ch {
  height: 20px;
  padding: 0 4px;
  font-size: 11px;
}
.hist {
  width: 100%;
  height: 80px;
  display: block;
  background: var(--bg-0);
}
.info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 8px;
  font-size: 10px;
  color: var(--fg-2);
  min-height: 34px;
}
.sw {
  display: inline-block;
  width: 10px;
  height: 10px;
  border: 1px solid var(--line-strong);
  vertical-align: middle;
}
</style>
